import { useState } from "react";
import { useDebouncedValue } from "@tellescope/ui";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import type { AttachFileDialogContentModel } from "./types";

export interface UseManagedContentVariantArgs {
  selectedContentIds: string[];
  dialogOpen: boolean;
  onAttachContent: (records: AttachFileDialogContentModel[]) => void;
}

export const useManagedContentVariant = ({
  selectedContentIds,
  dialogOpen,
  onAttachContent,
}: UseManagedContentVariantArgs) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedContentIds));
  const [contentSearch, setContentSearch] = useState("");
  const [debouncedContentSearch] = useDebouncedValue(contentSearch);

  const contentQuery = useTellescopeInfiniteQuery({
    model: "managed_content_records",
    method: "getSome",
    request: {
      search: debouncedContentSearch ? { query: debouncedContentSearch } : undefined,
      limit: 50,
    },
    enabled: dialogOpen,
    select: (data) => data.pages.flat().filter((record) => !record.forInternalUse),
    placeholderData: (prev) => prev,
  });

  const toggleContent = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const attach = () => {
    const records = contentQuery.data?.filter((record) => selectedIds.has(record.id)) ?? [];
    if (records.length > 0) {
      onAttachContent(records);
    }
  };

  const reset = () => {
    setSelectedIds(new Set());
    setContentSearch("");
  };

  return {
    selectedIds,
    contentSearch,
    setContentSearch,
    debouncedContentSearch,
    contentQuery,
    toggleContent,
    clearSelection,
    attach,
    reset,
    totalSelectedCount: selectedIds.size,
  };
};
